import React, { useContext, useState, useEffect } from 'react';
import { Input, Switch, Typography } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import SafetyCard from './SafetyCard';
import { ALERT_CATEGORY, triggerAlert } from '../../mqtt/aws';
import { SimulatorContext } from 'store/context';

const { Text } = Typography;

const EmergencyContactAlert = ({ messageApi, onTriggered }) => {
  const { stsToken: credentials, vehicleSimulatorSettings: { serialNo, vehicleKey } } = useContext(SimulatorContext);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [includeLocation, setIncludeLocation] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load emergency contact from localStorage on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('emergency_contact') || '{}');
      if (saved.name) setContactName(saved.name);
      if (saved.phone) setContactPhone(saved.phone);
      if (saved.includeLocation !== undefined) setIncludeLocation(saved.includeLocation);
    } catch {
      // ignore parse errors
    }
  }, []);

  // Save contact info to localStorage when changed
  const saveContact = () => {
    localStorage.setItem('emergency_contact', JSON.stringify({
      name: contactName,
      phone: contactPhone,
      includeLocation,
    }));
  };

  const triggerEmergencyAlert = async () => {
    if (!contactName || !contactPhone) {
      messageApi.warning('Please enter contact name and phone number');
      return;
    }

    // Save contact info
    saveContact();

    setLoading(true);
    const mockLocation = '40.7128°N, 74.0060°W'; // NYC coordinates as sample
    await triggerAlert(
      {
        serialNo,
        vehicleKey,
        actual: `SOS to ${contactName}: ${contactPhone}`,
        threshold: includeLocation ? `Location: ${mockLocation}` : 'No location',
      },
      credentials,
      ALERT_CATEGORY.EMERGENCY_CONTACT_ALERT
    )
      .then(() => {
        messageApi.success(`SOS sent to ${contactName} ${includeLocation ? 'with location' : ''}`);
        onTriggered?.({
          success: true,
          value: `${contactName} ${includeLocation ? '📍' : ''}`,
        });
      })
      .catch(() => {
        messageApi.error('Failed to send emergency alert');
        onTriggered?.({ success: false, value: contactName });
      })
      .finally(() => setLoading(false));
  };

  return (
    <SafetyCard
      title="Emergency Alert"
      icon={<PhoneOutlined />}
      iconBg="#fffbeb"
      iconColor="#d97706"
      protectionLevel="Emergency"
      onTrigger={triggerEmergencyAlert}
      triggerLabel="Send SOS"
      loading={loading}
    >
      <div>
        <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
          Contact Name
        </Text>
        <Input
          placeholder="e.g. Mom"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          onBlur={saveContact}
          maxLength={50}
        />
      </div>
      <div>
        <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
          Phone Number
        </Text>
        <Input
          placeholder="e.g. +1 (555) 123-4567"
          type="tel"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          onBlur={saveContact}
          maxLength={20}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 11, color: '#64748b' }}>Include Location</Text>
        <Switch
          checked={includeLocation}
          onChange={(checked) => {
            setIncludeLocation(checked);
            setTimeout(() => saveContact(), 0);
          }}
        />
      </div>
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 8,
          background: '#fffbeb',
          border: '1px solid #fde68a',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706', flexShrink: 0 }} />
        <Text style={{ fontSize: 11, color: '#b45309', fontWeight: 500 }}>
          Emergency SOS with real-time location{includeLocation ? ' enabled' : ' disabled'}
        </Text>
      </div>
    </SafetyCard>
  );
};

export default EmergencyContactAlert;
